import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, FilterIcon, XIcon, MusicIcon, BookOpenIcon, BarChartIcon, CheckIcon } from 'lucide-react';
// Define filter option types
interface FilterOptions {
  instruments: string[];
  eras: string[];
  difficulties: string[];
}
interface SheetMusicFiltersProps {
  darkMode: boolean;
  onSearchChange: (search: string) => void;
  onInstrumentChange: (instruments: string[]) => void;
  onEraChange: (eras: string[]) => void;
  onDifficultyChange: (difficulties: string[]) => void;
  filterOptions: FilterOptions;
  selectedInstruments: string[];
  selectedEras: string[];
  selectedDifficulties: string[];
}
export const SheetMusicFilters: React.FC<SheetMusicFiltersProps> = ({
  darkMode,
  onSearchChange,
  onInstrumentChange,
  onEraChange,
  onDifficultyChange,
  filterOptions,
  selectedInstruments,
  selectedEras,
  selectedDifficulties
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showInstrumentDropdown, setShowInstrumentDropdown] = useState(false);
  const [showEraDropdown, setShowEraDropdown] = useState(false);
  const [showDifficultyDropdown, setShowDifficultyDropdown] = useState(false);
  const instrumentDropdownRef = useRef(null);
  const eraDropdownRef = useRef(null);
  const difficultyDropdownRef = useRef(null);
  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    onSearchChange(value);
  };
  // Handle instrument selection
  const toggleInstrument = (instrument: string) => {
    if (selectedInstruments.includes(instrument)) {
      onInstrumentChange(selectedInstruments.filter(i => i !== instrument));
    } else {
      onInstrumentChange([...selectedInstruments, instrument]);
    }
  };
  // Handle era selection
  const toggleEra = (era: string) => {
    if (selectedEras.includes(era)) {
      onEraChange(selectedEras.filter(e => e !== era));
    } else {
      onEraChange([...selectedEras, era]);
    }
  };
  // Handle difficulty selection
  const toggleDifficulty = (difficulty: string) => {
    if (selectedDifficulties.includes(difficulty)) {
      onDifficultyChange(selectedDifficulties.filter(d => d !== difficulty));
    } else {
      onDifficultyChange([...selectedDifficulties, difficulty]);
    }
  };
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (instrumentDropdownRef.current && !(instrumentDropdownRef.current as any).contains(event.target) && !(event.target as any).closest('[data-dropdown="instrument"]')) {
        setShowInstrumentDropdown(false);
      }
      if (eraDropdownRef.current && !(eraDropdownRef.current as any).contains(event.target) && !(event.target as any).closest('[data-dropdown="era"]')) {
        setShowEraDropdown(false);
      }
      if (difficultyDropdownRef.current && !(difficultyDropdownRef.current as any).contains(event.target) && !(event.target as any).closest('[data-dropdown="difficulty"]')) {
        setShowDifficultyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Completely rewritten dropdown item rendering function with inline styles
  const renderDropdownItem = (key: string | number, label: string, isSelected: boolean, onClick: () => void) => {
    const bgColor = isSelected ? darkMode ? 'rgba(10, 17, 40, 0.8)' : 'rgba(254, 226, 226, 1)' : 'transparent';
    const textColor = isSelected ? darkMode ? 'white' : '#e63946' : darkMode ? 'white' : '#374151';
    const hoverBgColor = darkMode ? 'rgba(10, 17, 40, 0.5)' : 'rgba(243, 244, 246, 1)';
    return <div key={key} onClick={onClick} style={{
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      padding: '10px 16px',
      cursor: 'pointer',
      backgroundColor: bgColor,
      color: textColor,
      borderRadius: '8px',
      margin: '2px 0',
      textAlign: 'left',
      position: 'relative'
    }} onMouseOver={e => {
      if (!isSelected) {
        e.currentTarget.style.backgroundColor = hoverBgColor;
      }
    }} onMouseOut={e => {
      if (!isSelected) {
        e.currentTarget.style.backgroundColor = 'transparent';
      }
    }} data-id="element-11174">
        <div style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        justifyContent: 'flex-start'
      }} data-id="element-11175">
          <div style={{
          width: '24px',
          marginRight: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }} data-id="element-11176">
            {isSelected && <CheckIcon className="text-deep-pink" style={{
            width: '16px',
            height: '16px',
            color: '#e63946',
            flexShrink: 0
          }} data-id="element-11177" />}
          </div>
          <span style={{
          display: 'block',
          textAlign: 'left',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }} data-id="element-11178">
            {label}
          </span>
        </div>
      </div>;
  };
  return <div className={`p-6 rounded-xl ${darkMode ? 'bg-navy-800/80' : 'bg-white'} shadow-sm mb-6`} data-id="element-11179">
      <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:items-center md:space-x-4" data-id="element-11180">
        {/* Search Bar */}
        <div className="w-full md:w-[40%] relative" data-id="element-11181">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-11182">
            <SearchIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11183" />
          </div>
          <input type="text" value={searchQuery} onChange={handleSearchChange} placeholder="Search by title, composer..." className={`block w-full pl-10 pr-4 py-2.5 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'} border focus:ring-deep-pink focus:border-deep-pink`} data-id="element-11184" />
        </div>
        {/* Instrument Filter */}
        <div className="relative w-full md:w-[20%]" data-id="element-11185">
          <button data-dropdown="instrument" onClick={() => {
          setShowInstrumentDropdown(!showInstrumentDropdown);
          setShowEraDropdown(false);
          setShowDifficultyDropdown(false);
        }} className={`flex items-center justify-between w-full px-4 py-2.5 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white hover:bg-navy-800' : 'bg-gray-50 border-gray-300 text-gray-900 hover:bg-gray-100'} border transition-colors`} data-id="element-11186">
            <div className="flex items-center" data-id="element-11187">
              <MusicIcon className="h-4 w-4 mr-2 flex-shrink-0" data-id="element-11188" />
              <span className="truncate" data-id="element-11189">
                {selectedInstruments.length === 0 ? 'All Instruments' : selectedInstruments.length === 1 ? selectedInstruments[0] : `${selectedInstruments.length} Instruments`}
              </span>
            </div>
            <FilterIcon className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex-shrink-0`} data-id="element-11190" />
          </button>
          {showInstrumentDropdown && <div ref={instrumentDropdownRef} className={`absolute z-10 mt-2 w-full rounded-lg shadow-lg ${darkMode ? 'bg-navy-800 border border-navy-700' : 'bg-white border-gray-200'} border overflow-hidden`} style={{
          textAlign: 'left'
        }} data-id="element-11191">
              <div className={`p-3 max-h-60 overflow-y-auto ${darkMode ? 'scrollbar-dark' : 'scrollbar-light'}`} style={{
            textAlign: 'left'
          }} data-id="element-11192">
                {filterOptions.instruments.length > 0 ? filterOptions.instruments.map((instrument, index) => renderDropdownItem(index, instrument, selectedInstruments.includes(instrument), () => toggleInstrument(instrument))) : <div className={`text-sm text-center py-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11193">
                    No instruments available
                  </div>}
              </div>
              {selectedInstruments.length > 0 && <div className={`p-2 border-t ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-11194">
                  <button onClick={() => onInstrumentChange([])} className={`text-xs w-full py-1.5 rounded ${darkMode ? 'bg-navy-700 text-gray-300 hover:bg-navy-900' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-11195">
                    Clear All
                  </button>
                </div>}
            </div>}
        </div>
        {/* Era/Style Filter */}
        <div className="relative w-full md:w-[20%]" data-id="element-11196">
          <button data-dropdown="era" onClick={() => {
          setShowEraDropdown(!showEraDropdown);
          setShowInstrumentDropdown(false);
          setShowDifficultyDropdown(false);
        }} className={`flex items-center justify-between w-full px-4 py-2.5 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white hover:bg-navy-800' : 'bg-gray-50 border-gray-300 text-gray-900 hover:bg-gray-100'} border transition-colors`} data-id="element-11197">
            <div className="flex items-center" data-id="element-11198">
              <BookOpenIcon className="h-4 w-4 mr-2 flex-shrink-0" data-id="element-11199" />
              <span className="truncate" data-id="element-11200">
                {selectedEras.length === 0 ? 'All Eras' : selectedEras.length === 1 ? selectedEras[0] : `${selectedEras.length} Eras`}
              </span>
            </div>
            <FilterIcon className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex-shrink-0`} data-id="element-11201" />
          </button>
          {showEraDropdown && <div ref={eraDropdownRef} className={`absolute z-10 mt-2 w-full rounded-lg shadow-lg ${darkMode ? 'bg-navy-800 border border-navy-700' : 'bg-white border-gray-200'} border overflow-hidden`} style={{
          textAlign: 'left'
        }} data-id="element-11202">
              <div className={`p-3 max-h-60 overflow-y-auto ${darkMode ? 'scrollbar-dark' : 'scrollbar-light'}`} style={{
            textAlign: 'left'
          }} data-id="element-11203">
                {filterOptions.eras.length > 0 ? filterOptions.eras.map((era, index) => renderDropdownItem(index, era, selectedEras.includes(era), () => toggleEra(era))) : <div className={`text-sm text-center py-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11204">
                    No eras available
                  </div>}
              </div>
              {selectedEras.length > 0 && <div className={`p-2 border-t ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-11205">
                  <button onClick={() => onEraChange([])} className={`text-xs w-full py-1.5 rounded ${darkMode ? 'bg-navy-700 text-gray-300 hover:bg-navy-900' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-11206">
                    Clear All
                  </button>
                </div>}
            </div>}
        </div>
        {/* Difficulty Filter */}
        <div className="relative w-full md:w-[20%]" data-id="element-11207">
          <button data-dropdown="difficulty" onClick={() => {
          setShowDifficultyDropdown(!showDifficultyDropdown);
          setShowInstrumentDropdown(false);
          setShowEraDropdown(false);
        }} className={`flex items-center justify-between w-full px-4 py-2.5 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white hover:bg-navy-800' : 'bg-gray-50 border-gray-300 text-gray-900 hover:bg-gray-100'} border transition-colors`} data-id="element-11208">
            <div className="flex items-center" data-id="element-11209">
              <BarChartIcon className="h-4 w-4 mr-2 flex-shrink-0" data-id="element-11210" />
              <span className="truncate" data-id="element-11211">
                {selectedDifficulties.length === 0 ? 'All Difficulties' : selectedDifficulties.length === 1 ? selectedDifficulties[0] : `${selectedDifficulties.length} Levels`}
              </span>
            </div>
            <FilterIcon className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex-shrink-0`} data-id="element-11212" />
          </button>
          {showDifficultyDropdown && <div ref={difficultyDropdownRef} className={`absolute z-10 mt-2 w-full rounded-lg shadow-lg ${darkMode ? 'bg-navy-800 border border-navy-700' : 'bg-white border-gray-200'} border overflow-hidden`} style={{
          textAlign: 'left'
        }} data-id="element-11213">
              <div className={`p-3 max-h-60 overflow-y-auto ${darkMode ? 'scrollbar-dark' : 'scrollbar-light'}`} style={{
            textAlign: 'left'
          }} data-id="element-11214">
                {filterOptions.difficulties.map((difficulty, index) => renderDropdownItem(index, difficulty, selectedDifficulties.includes(difficulty), () => toggleDifficulty(difficulty)))}
              </div>
              {selectedDifficulties.length > 0 && <div className={`p-2 border-t ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-11215">
                  <button onClick={() => onDifficultyChange([])} className={`text-xs w-full py-1.5 rounded ${darkMode ? 'bg-navy-700 text-gray-300 hover:bg-navy-900' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-11216">
                    Clear All
                  </button>
                </div>}
            </div>}
        </div>
      </div>
      {/* Selected Filters */}
      {(selectedInstruments.length > 0 || selectedEras.length > 0 || selectedDifficulties.length > 0) && <div className="flex flex-wrap gap-2 mt-4" data-id="element-11217">
          {selectedInstruments.map((instrument, index) => <div key={`instrument-${index}`} className={`flex items-center text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-navy-900/50 text-white' : 'bg-gray-100 text-gray-800'}`} data-id="element-11218">
              <span data-id="element-11219">{instrument}</span>
              <button onClick={() => toggleInstrument(instrument)} className="ml-1.5 text-gray-400 hover:text-gray-600" data-id="element-11220">
                <XIcon className="h-3 w-3" data-id="element-11221" />
              </button>
            </div>)}
          {selectedEras.map((era, index) => <div key={`era-${index}`} className={`flex items-center text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-navy-900/50 text-white' : 'bg-gray-100 text-gray-800'}`} data-id="element-11222">
              <span data-id="element-11223">{era}</span>
              <button onClick={() => toggleEra(era)} className="ml-1.5 text-gray-400 hover:text-gray-600" data-id="element-11224">
                <XIcon className="h-3 w-3" data-id="element-11225" />
              </button>
            </div>)}
          {selectedDifficulties.map((difficulty, index) => <div key={`difficulty-${index}`} className={`flex items-center text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-navy-900/50 text-white' : 'bg-gray-100 text-gray-800'}`} data-id="element-11226">
              <span data-id="element-11227">{difficulty}</span>
              <button onClick={() => toggleDifficulty(difficulty)} className="ml-1.5 text-gray-400 hover:text-gray-600" data-id="element-11228">
                <XIcon className="h-3 w-3" data-id="element-11229" />
              </button>
            </div>)}
          {(selectedInstruments.length > 0 || selectedEras.length > 0 || selectedDifficulties.length > 0) && <button onClick={() => {
        onInstrumentChange([]);
        onEraChange([]);
        onDifficultyChange([]);
      }} className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`} data-id="element-11230">
              Clear all filters
            </button>}
        </div>}
    </div>;
};